// ============================================================
// TraitsCard - character traits list for both Overview sheets.
// When a live target is provided (defender), mechanical traits
// whose flat target field was disabled in the Stats tab are
// hidden, and numeric parameters prefer the live field value.
// Non-mechanical traits carry a (Narrative) tag.
// ============================================================
import { memo } from 'react';
import sheet from '../../styles/ActionSheet.module.css';
import styles from '../../styles/OverviewSheet.module.css';
import { getTrait } from '../../engine/character/traitCatalog';
import { traitText } from '../../engine/character/instanceText';
import type { Character, TraitInstance } from '../../engine/character/types';
import type { Target } from '../../types';

/** Map traitId → flat target field for mechanical traits with a Stats-tab toggle. */
const TRAIT_FIELD_MAP: Record<string, string> = {
  true_grit: 'trueGrit',
  stuff_of_nightmares: 'stuffOfNightmares',
  incorporeal: 'incorporeal',
  from_beyond: 'fromBeyond',
  astartes: 'astartes',
  undying: 'undying',
  daemonic: 'daemonicProtection',
  machine: 'machineAP',
  natural_armour: 'naturalArmour',
  regeneration: 'regeneration',
  fear: 'fearRating',
};

/** Check if a mechanical trait is still active on the target (flat field is truthy). */
function isTraitActiveOnTarget(traitId: string, target: Target): boolean {
  const field = TRAIT_FIELD_MAP[traitId];
  if (!field) return true; // No flat-field mapping → treat as active (narrative trait)
  return !!(target as unknown as Record<string, unknown>)[field];
}

interface TraitsCardProps {
  character: Character;
  /** Live target for trait filtering + parameter values (defender side). */
  target?: Target | null;
}

/**
 * The parameter to show: the target's LIVE flat field when it holds a
 * positive number (Stats-tab edits change it post-spawn), else the
 * character's own trait parameter.
 */
function shownParam(inst: TraitInstance, target: Target | null | undefined): string | undefined {
  const field = TRAIT_FIELD_MAP[inst.traitId];
  const liveVal = target && field ? (target as unknown as Record<string, unknown>)[field] : undefined;
  return typeof liveVal === 'number' && liveVal > 0 ? String(liveVal) : inst.parameter;
}

function TraitEntry({ inst, target }: { inst: TraitInstance; target?: Target | null }) {
  const text = traitText(inst);
  if (!text) return null;
  // A custom trait has no catalog entry, so it is never mechanical.
  const mechanical = !!getTrait(inst.traitId)?.mechanical;
  if (target && mechanical && !isTraitActiveOnTarget(inst.traitId, target)) return null;
  const param = shownParam(inst, target);
  return (
    <div className={sheet.textListEntry}>
      <span className={sheet.textListName}>{text.name}</span>
      {param && <span className={sheet.textListParam}>({param})</span>}
      {!mechanical && <span className={styles.narrativeTag}>{text.custom ? 'Custom' : 'Narrative'}</span>}
      <div className={sheet.textListDesc}>{text.description}</div>
    </div>
  );
}

function TraitsCard({ character, target }: TraitsCardProps) {
  const traits = character.traits ?? [];
  if (traits.length === 0) return null;

  return (
    <div className={styles.card} data-testid="traits-card">
      <div className={styles.cardTitle}>Traits</div>
      <div className={styles.scrollList}>
        {traits.map((t, i) => (
          // By index: one catalog trait may be taken more than once.
          <TraitEntry key={`${i}-${t.traitId}`} inst={t} target={target} />
        ))}
      </div>
    </div>
  );
}

export default memo(TraitsCard);
